import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** An activity-style ring. `value` clamps to 0..max; nothing ever goes negative. */
export function Ring({
  value,
  max = 100,
  color,
  size = 44,
  stroke,
  label,
  children,
}: {
  value: number;
  max?: number;
  color: string;
  size?: number;
  stroke?: number;
  label: string;
  children?: ReactNode;
}): JSX.Element {
  const w = stroke ?? Math.max(4, Math.round(size / 8));
  const r = (size - w) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  return (
    <span
      className="ui-ring"
      style={{ width: size, height: size }}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={0.22} strokeWidth={w} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={w}
          strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children && <span className="ui-ring__center">{children}</span>}
    </span>
  );
}

/** A number with its caption (and optionally a ring) — health/money readouts. */
export function Metric({
  label,
  value,
  unit,
  ring,
  to,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  ring?: { value: number; max: number; color: string };
  to?: string;
}): JSX.Element {
  const inner = (
    <>
      {ring && <Ring value={ring.value} max={ring.max} color={ring.color} size={36} label={`${label} ${String(value)}`} />}
      <span className="ui-metric__text">
        <span className="ui-metric__value num">
          {value}
          {unit && <span className="ui-metric__unit">{unit}</span>}
        </span>
        <span className="ui-metric__label">{label}</span>
      </span>
    </>
  );
  return to ? (
    <Link to={to} className="ui-metric">
      {inner}
    </Link>
  ) : (
    <div className="ui-metric">{inner}</div>
  );
}
